import { getStudentData, saveStudentData } from '../assets/data';
import { getLessonListForStudent, updateLessonStatusById, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';
import { saveScheduleItem } from '../utils/scheduleService';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). This is the Lessons tab's own status-change
// handler — source: 'lessons' in the dispatched event, which
// useScheduleSync's listener deliberately ignores for auto-charging
// purposes (only the Schedule tab's status change, source: 'schedule',
// may add/remove a charge). Moved byte-for-byte identical on purpose.
export function useLessonsTabStatusChange({
  studentId,
  navigate,
  studentLessons,
  setStudentLessons,
  setStatusChangeLoading,
  updateUpcomingLessons,
}) {
  const handleCreateLesson = () => {
    navigate(`/lessons/new?studentId=${studentId}`);
  };

  // Функция для изменения статуса урока
  const handleLessonStatusChange = async (lessonId, newStatus) => {
    const lessonToUpdate = studentLessons.find(lesson => lesson.id === lessonId);
    if (!lessonToUpdate) {
      console.error('Lesson not found:', lessonId);
      return;
    }
    setStatusChangeLoading(true);
    try {
    // Обновляем статус в списке уроков студента
    setStudentLessons(prev => prev.map(lesson =>
      lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
    ));

    // Обновляем в Supabase
    // Проверяем, является ли это уроком из lessons или из schedule
    // Если урок из schedule (не имеет полного контента), обновляем только в localStorage
    const hasFullContent = lessonToUpdate.containers || lessonToUpdate.editorContents || lessonToUpdate.whiteboards;

    if (hasFullContent) {
      // Это урок из lessons - обновляем в Supabase. Только статус —
      // lessonToUpdate здесь взят из studentLessons (getLessonListForStudent,
      // без content), полноценный saveLesson() перезаписал бы content урока
      // пустым значением и стирал бы его.
      try {
        const ok = await updateLessonStatusById(lessonId, newStatus);
        if (!ok) {
          console.log('Lesson not found in lessons table, may be in schedule only:', lessonId);
        } else {
          console.log('Lesson status updated in Supabase:', lessonId, 'to', newStatus);
        }
      } catch (error) {
        console.error('Error updating lesson status in Supabase:', error);
        setStudentLessons(prev => prev.map(lesson =>
          lesson.id === lessonId ? { ...lesson, status: lessonToUpdate.status } : lesson
        ));
      }
      // Deliberately NOT mirrored onto the schedule row: the Lessons tab's
      // status is just a progress indicator (one lesson can span several
      // taught sessions), and the schedule table's own status is what
      // drives auto-charging — mirroring this here would let a Lessons-tab
      // change add/remove a charge on the next reconciliation pass even
      // though the immediate listener above already correctly ignores it.
    } else {
      // Это запись из schedule — обновляем в Supabase (schedule)
      try {
        await saveScheduleItem({ ...lessonToUpdate, status: newStatus });
        console.log('Schedule lesson status updated in Supabase:', lessonId, 'to', newStatus);
      } catch (err) {
        console.error('Error updating schedule lesson status:', err);
        setStudentLessons(prev => prev.map(lesson =>
          lesson.id === lessonId ? { ...lesson, status: lessonToUpdate.status } : lesson
        ));
      }
    }

    // Broadcast so the in-mount payment listener can react and so any other
    // mounted view (e.g. Home as we add the listener there) refreshes.
    // prevStatus comes from the snapshot captured at the start of the
    // handler — this handler also pre-updates studentLessons via setState,
    // and by the time the listener reads its ref the new status may have
    // landed there already, making a ref-based prevStatus comparison
    // falsely report "no change".
    window.dispatchEvent(new CustomEvent('lessonStatusUpdated', {
      detail: { lessonId, status: newStatus, prevStatus: lessonToUpdate.status, source: 'lessons' }
    }));

    // Обновляем в основном хранилище novas_lessons_data
    const allLessons = getLessonsFromLocalStorage();
    const updatedLessons = allLessons.map(lesson =>
      lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
    );
    saveLessonsToLocalStorage(updatedLessons);

    // Обновляем урок в профиле студента, если он там есть
    const localStudentRecord = getStudentData()[studentId];
    if (localStudentRecord && localStudentRecord.lessons) {
      const updatedStudentData = {
        ...localStudentRecord,
        lessons: localStudentRecord.lessons.map(lesson =>
          lesson.id === lessonId ? { ...lesson, status: newStatus } : lesson
        )
      };

      const allStudents = getStudentData();
      allStudents[studentId] = updatedStudentData;
      saveStudentData(allStudents);
    }

    // Принудительно обновляем список уроков и upcoming lessons после изменения статуса
    setTimeout(() => {
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
      if (studentIdForLessons) {
        getLessonListForStudent(studentIdForLessons).then(lessons => {
          setStudentLessons(lessons);
          updateUpcomingLessons(); // Обновляем upcoming lessons
        }).catch(error => {
          console.error('Error loading lessons:', error);
        });
      }
    }, 100);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  return { handleCreateLesson, handleLessonStatusChange };
}
